// DNA CONNECT - Área Administrativa
// Dashboard e gestão do portal das famílias

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Users, Shield, Settings, Bell, FileText, Calendar, MessageCircle,
  Loader2, Search, Plus, RefreshCw, Download, Eye, Lock, Unlock,
  CheckCircle, XCircle, Clock, BarChart3, TrendingUp, AlertTriangle,
  Copy, Key, Mail, Image, Upload, Edit, Trash2, FolderOpen, Phone, ExternalLink, X
} from 'lucide-react';

import DocumentosAdmin from './DnaConnect/DocumentosAdmin';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = window.location.origin;

export default function DnaConnectAdmin() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [createUserModal, setCreateUserModal] = useState(false);
  const [bulkGenerateModal, setBulkGenerateModal] = useState(false);
  const [bulkByClassModal, setBulkByClassModal] = useState(false);
  const [turmasDisponiveis, setTurmasDisponiveis] = useState([]);
  const [turmasSelecionadas, setTurmasSelecionadas] = useState([]);
  const [featureModal, setFeatureModal] = useState(null);
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState(null); // { user, message, phone }
  const [whatsappConfirm, setWhatsappConfirm] = useState(null); // { user, bulk } — modal de confirmação antes de gerar senha
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappBulkModal, setWhatsappBulkModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    if (bulkByClassModal && turmasDisponiveis.length === 0) {
      api.get('/class-groups').then(r => setTurmasDisponiveis(r.data || [])).catch(() => {});
    }
  }, [bulkByClassModal]);


  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard' || activeTab === 'usuarios') {
        const response = await api.get('/dnaconnect/admin/usuarios?limit=5000');
        setUsers(response.data.users || []);
        
        // Usar totais reais do banco
        setStats({
          total: response.data.total || 0,
          ativos: response.data.total_active || 0,
          pendentes: response.data.total_pending || 0,
          bloqueados: response.data.total_blocked || 0
        });
      }
      
      if (activeTab === 'liberacao') {
        const response = await api.get('/dnaconnect/admin/features');
        setFeatures(response.data || []);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      await api.put(`/dnaconnect/admin/usuarios/${userId}/status?status=${newStatus}`);
      toast.success('Status atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Ativar/Desativar TODOS os usuários família
  const handleBulkStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Tem certeza que deseja ${newStatus === 'active' ? 'ATIVAR' : 'DESATIVAR'} TODOS os usuários família?`)) {
      return;
    }
    try {
      const response = await api.post('/dnaconnect/admin/usuarios/bulk-status', {
        status: newStatus,
        user_ids: null // null = todos
      });
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status em massa');
    }
  };

  // Redefinição por LINK seguro (nunca senha pronta por e-mail)
  const handleResetPassword = async (userId) => {
    try {
      const { data } = await api.post(`/dnaconnect/admin/usuarios/${userId}/enviar-acesso?first_access=false`);
      toast.success(data.message || 'Link de redefinição enviado por e-mail.');
      loadData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao enviar a redefinição de senha.');
    }
  };

  // Reenviar acesso = novo link de primeiro acesso (nunca cria outro usuário)
  const handleResendCredentials = async (userId) => {
    try {
      const { data } = await api.post(`/dnaconnect/admin/usuarios/${userId}/enviar-acesso?first_access=true`);
      toast.success(data.message || 'Acesso reenviado por e-mail.');
      loadData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao reenviar o acesso.');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Excluir permanentemente o usuário ${email}?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/dnaconnect/admin/usuarios/${userId}`);
      toast.success('Usuário excluído');
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // ── WhatsApp: envio manual de credenciais (apenas abre wa.me — não dispara API)
  const cleanPhoneForWa = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) return '';
    // Garante DDI 55 (Brasil)
    return digits.startsWith('55') ? digits : `55${digits}`;
  };

  const buildWhatsappMessageFallback = (user, password) => {
    const nome = user.responsible_name || 'Responsável';
    const alunos = (user.students || []).map((s) => `• ${s.name}`).join('\n') || '—';
    return (
`Olá, ${nome}! 👋

Seja bem-vindo(a) ao DNA Connect, o portal das famílias do Novo DNA Instituto de Aprendizagem.

Através dele você poderá acompanhar:

✅ Informações acadêmicas
✅ Boletins e notas
✅ Frequência
✅ Comunicados
✅ Documentos
✅ Eventos e calendário escolar
✅ Informações financeiras

🔗 Acesso ao Portal:
https://dnacore.novodna.com.br/familia

👤 Usuário:
${user.email}

🔑 Senha:
${password}

👨‍🎓 Aluno(s):
${alunos}

Por segurança, recomendamos alterar sua senha após o primeiro acesso.

Em caso de dúvidas, nossa equipe estará à disposição para auxiliá-lo(a).

Atenciosamente,

Novo DNA Instituto de Aprendizagem
🧬 Muitas mentes, Uma missão.`
    );
  };

  const openWhatsappModal = (user) => {
    if (!user.responsible_phone) { toast.error('Responsável sem telefone cadastrado'); return; }
    if (!user.email) { toast.error('Usuário sem login cadastrado'); return; }
    // Abre modal de confirmação ANTES de gerar a senha
    setWhatsappConfirm({ user });
  };

  const confirmAndGeneratePassword = async () => {
    if (!whatsappConfirm) return;
    const { user } = whatsappConfirm;
    setWhatsappLoading(true);
    let operator_email = '';
    try { operator_email = JSON.parse(localStorage.getItem('user') || '{}').email || ''; } catch { /* noop */ }
    try {
      const r = await api.post(`/dnaconnect/admin/usuarios/${user.id}/wa-generate-password`, { operator_email });
      const data = r.data || {};
      setWhatsappConfirm(null);
      setWhatsappModal({
        user: { ...user, responsible_name: data.responsible_name || user.responsible_name, students: (data.students || []).map((n) => ({ name: n })) },
        phone: data.phone || user.responsible_phone,
        password: data.password,
        message: data.message || buildWhatsappMessageFallback(user, data.password),
      });
      toast.success('Nova senha gerada. Revise a mensagem antes de abrir o WhatsApp.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao gerar nova senha.');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const confirmWhatsappOpen = async () => {
    if (!whatsappModal) return;
    const { user, phone, message } = whatsappModal;
    const waPhone = cleanPhoneForWa(phone);
    if (!waPhone) { toast.error('Telefone inválido'); return; }
    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`;
    let operator_email = '';
    try { operator_email = JSON.parse(localStorage.getItem('user') || '{}').email || ''; } catch { /* noop */ }
    const students = (user.students || []).map((s) => s.name);
    api.post(`/dnaconnect/admin/usuarios/${user.id}/whatsapp-audit`, { phone, students, operator_email }).catch(() => { /* noop */ });
    window.open(url, '_blank', 'noopener,noreferrer');
    setWhatsappModal(null);
  };

  const copyWhatsappMessage = async () => {
    if (!whatsappModal) return;
    const { user, message } = whatsappModal;
    let operator_email = '';
    try { operator_email = JSON.parse(localStorage.getItem('user') || '{}').email || ''; } catch { /* noop */ }
    let fallback = false;
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Mensagem copiada com sucesso!');
    } catch {
      fallback = true;
      try {
        const ta = document.querySelector('[data-testid="wa-message-input"]');
        if (ta) { ta.focus(); ta.select(); }
      } catch { /* noop */ }
      toast.error('Copie manualmente com Ctrl+C');
    }
    api.post(`/dnaconnect/admin/usuarios/${user.id}/message-copied-audit`, { operator_email, fallback }).catch(() => { /* noop */ });
  };

  const openBulkWhatsappLinks = () => {
    const elig = (filteredUsers || []).filter((u) => !!u.responsible_phone && !!u.email);
    if (elig.length === 0) { toast.error('Nenhum usuário filtrado com telefone e login cadastrados.'); return; }
    setWhatsappBulkModal(true);
  };

  const openBulkLink = async (user) => {
    if (!user.responsible_phone) { toast.error('Responsável sem telefone cadastrado'); return; }
    if (!user.email) { toast.error('Usuário sem login cadastrado'); return; }
    let operator_email = '';
    try { operator_email = JSON.parse(localStorage.getItem('user') || '{}').email || ''; } catch { /* noop */ }
    try {
      const r = await api.post(`/dnaconnect/admin/usuarios/${user.id}/wa-generate-password`, { operator_email, bulk: true });
      const data = r.data || {};
      const waPhone = cleanPhoneForWa(data.phone || user.responsible_phone);
      if (!waPhone) { toast.error('Telefone inválido'); return; }
      const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(data.message)}`;
      api.post(`/dnaconnect/admin/usuarios/${user.id}/whatsapp-audit`, { phone: data.phone, students: data.students || [], operator_email, bulk: true }).catch(() => { /* noop */ });
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(`Senha nova gerada para ${user.responsible_name || user.email}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao gerar senha p/ ' + (user.responsible_name || user.email));
    }
  };

  const handleBulkGenerateByClass = async (turmaIds, sendEmail = true) => {
    try {
      const response = await api.post('/dnaconnect/admin/usuarios/gerar-por-turma', {
        turma_ids: turmaIds, send_email: sendEmail
      });
      setCredentialsModal({
        type: 'bulk', created: response.data.created,
        emails_sent: response.data.emails_sent || 0,
        credentials: response.data.credentials
      });
      toast.success(`${response.data.created} usuários criados`);
      loadData();
    } catch (error) {
      toast.error('Erro na geração por turma');
    }
  };

  const handleBulkGenerate = async (sendEmail = true, portalUrl = '') => {
    try {
      const response = await api.post('/dnaconnect/admin/usuarios/gerar-em-massa', {
        send_email: sendEmail,
        portal_url: portalUrl
      });
      setCredentialsModal({
        type: 'bulk',
        created: response.data.created,
        emails_sent: response.data.emails_sent,
        credentials: response.data.credentials
      });
      toast.success(`${response.data.created} usuários criados${sendEmail ? ` e ${response.data.emails_sent} emails enviados` : ''}`);
      loadData();
    } catch (error) {
      toast.error('Erro na geração em massa');
    }
  };

  const handleImpersonate = async (userId, userName) => {
    try {
      const response = await api.post(`/dnaconnect/admin/usuarios/${userId}/impersonate`);
      const { token, user } = response.data;
      
      // Abrir nova aba com o portal da família
      const familyUrl = `${window.location.origin}/familia?impersonate_token=${token}`;
      
      // Armazenar dados em sessionStorage para a nova aba poder ler
      const impersonateData = {
        token,
        user,
        isImpersonation: true,
        adminReturnUrl: window.location.href
      };
      
      // Usar uma chave única para evitar conflitos
      sessionStorage.setItem('dnaconnect_impersonate', JSON.stringify(impersonateData));
      
      toast.success(`Abrindo portal como ${userName}...`);
      
      // Abrir em nova aba
      window.open(familyUrl, '_blank');
    } catch (error) {
      toast.error('Erro ao visualizar como família');
    }
  };

  const handleToggleFeature = async (featureId, enabled) => {
    try {
      await api.patch(`/dnaconnect/admin/features/${featureId}/toggle`, { enabled });
      toast.success('Funcionalidade atualizada');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleEditFeature = (feature) => {
    setFeatureModal({ open: true, feature: feature });
  };

  const handleDeleteFeature = async (featureId) => {
    if (!confirm('Tem certeza que deseja excluir esta regra?')) return;
    
    try {
      await api.delete(`/dnaconnect/admin/features/${featureId}`);
      toast.success('Regra excluída com sucesso');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir regra');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.responsible_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.students?.some(s => s.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
      pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
      blocked: { label: 'Bloqueado', color: 'bg-red-100 text-red-700' },
      inactive: { label: 'Inativo', color: 'bg-gray-100 text-gray-700' }
    };
    const cfg = config[status] || config.inactive;
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">DNA CONNECT Admin</h1>
            <p className="text-gray-500">Gestão do Portal das Famílias</p>
          </div>
        </div>
        
        <Button onClick={() => loadData()} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="usuarios">
            <Users className="w-4 h-4 mr-2" />
            Usuários
          </TabsTrigger>
          <TabsTrigger value="liberacao">
            <Settings className="w-4 h-4 mr-2" />
            Central de Liberação
          </TabsTrigger>
          <TabsTrigger value="comunicacao">
            <MessageCircle className="w-4 h-4 mr-2" />
            Comunicação
          </TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">
            <FolderOpen className="w-4 h-4 mr-2" />
            Documentos
          </TabsTrigger>
          <TabsTrigger value="ferramentas">
            <Settings className="w-4 h-4 mr-2" />
            Ferramentas
          </TabsTrigger>
          <TabsTrigger value="logs">
            <FileText className="w-4 h-4 mr-2" />
            Logs
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <>
            {/* Dashboard */}
            <TabsContent value="dashboard" className="space-y-6">
              {/* Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total de Famílias</p>
                        <p className="text-3xl font-bold text-purple-700">{stats?.total || 0}</p>
                      </div>
                      <Users className="w-10 h-10 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Ativos</p>
                        <p className="text-3xl font-bold text-green-700">{stats?.ativos || 0}</p>
                      </div>
                      <CheckCircle className="w-10 h-10 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Aguardando 1º Acesso</p>
                        <p className="text-3xl font-bold text-yellow-700">{stats?.pendentes || 0}</p>
                      </div>
                      <Clock className="w-10 h-10 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Bloqueados</p>
                        <p className="text-3xl font-bold text-red-700">{stats?.bloqueados || 0}</p>
                      </div>
                      <XCircle className="w-10 h-10 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Ações Rápidas */}
              <Card>
                <CardHeader>
                  <CardTitle>Ações Rápidas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => setCreateUserModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Usuário
                    </Button>
                    <Button variant="outline" onClick={() => setBulkGenerateModal(true)}>
                      <Users className="w-4 h-4 mr-2" />
                      Gerar em Massa
                    </Button>
                    <Button variant="outline" onClick={() => setBulkByClassModal(true)}>
                      <Calendar className="w-4 h-4 mr-2" />
                      Gerar por Turma
                    </Button>
                    <Link to="/dnaconnect-admin/comunicacao">
                      <Button variant="outline">
                        <Bell className="w-4 h-4 mr-2" />
                        Enviar Comunicado
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Últimos Usuários */}
              <Card>
                <CardHeader>
                  <CardTitle>Últimos Usuários Criados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.slice(0, 5).map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="font-bold text-purple-600">
                              {user.responsible_name?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.responsible_name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        {getStatusBadge(user.status)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Usuários */}
            <TabsContent value="usuarios" className="space-y-4">
              {/* Filtros e Ações em Massa */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por nome ou email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="active">Ativos</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                      <SelectItem value="blocked">Bloqueados</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setCreateUserModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Novo
                  </Button>
                </div>
                
                {/* Ações em Massa */}
                <div className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg border">
                  <span className="text-sm font-medium text-gray-600 mr-2">Ações em Massa:</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                    onClick={() => handleBulkStatusUpdate('active')}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Ativar Todos
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                    onClick={() => handleBulkStatusUpdate('inactive')}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Desativar Todos
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                    onClick={openBulkWhatsappLinks}
                    data-testid="btn-bulk-wa"
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Enviar credenciais WhatsApp
                  </Button>
                  <span className="text-xs text-gray-500 ml-auto">
                    {stats?.total || 0} usuário(s) cadastrado(s)
                  </span>
                </div>
              </div>

              {/* Lista */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Responsável</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Alunos</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user) => (
                          <tr key={user.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">{user.responsible_name}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                            <td className="py-3 px-4">
                              <div className="flex flex-wrap gap-1">
                                {user.students?.slice(0, 2).map((s, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {s.name?.split(' ')[0]}
                                  </Badge>
                                ))}
                                {user.students?.length > 2 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{user.students.length - 2}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">{getStatusBadge(user.status)}</td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleImpersonate(user.id, user.responsible_name)}
                                  title="Visualizar como Família"
                                  className="hover:bg-purple-100"
                                >
                                  <Eye className="w-4 h-4 text-purple-600" />
                                </Button>
                                {user.status === 'active' ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleUpdateStatus(user.id, 'blocked')}
                                    title="Bloquear"
                                  >
                                    <Lock className="w-4 h-4 text-red-600" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleUpdateStatus(user.id, 'active')}
                                    title="Ativar"
                                  >
                                    <Unlock className="w-4 h-4 text-green-600" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleResetPassword(user.id)}
                                  title="Resetar Senha"
                                >
                                  <Key className="w-4 h-4 text-amber-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleResendCredentials(user.id)}
                                  title="Reenviar Credenciais"
                                >
                                  <Mail className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openWhatsappModal(user)}
                                  disabled={!user.responsible_phone}
                                  title={user.responsible_phone ? 'Enviar credenciais via WhatsApp' : 'Responsável sem telefone cadastrado'}
                                  className={user.responsible_phone ? 'hover:bg-green-50' : ''}
                                  data-testid={`btn-wa-${user.id}`}
                                >
                                  <Phone className={`w-4 h-4 ${user.responsible_phone ? 'text-green-600' : 'text-gray-300'}`} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteUser(user.id, user.email)}
                                  title="Excluir Usuário"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Central de Liberação */}
            <TabsContent value="liberacao" className="space-y-4">
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Settings className="w-6 h-6 text-purple-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-purple-900">Central de Liberação</h3>
                      <p className="text-sm text-purple-700">
                        Controle quais funcionalidades cada família pode acessar. 
                        Você pode criar regras por módulo, segmento, turma ou aluno.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={() => setFeatureModal({ isNew: true })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Regra
                </Button>
              </div>

              {/* Lista de Features */}
              <div className="grid gap-4">
                {features.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Settings className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">Nenhuma regra configurada</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Por padrão, todas as funcionalidades estão liberadas.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  features.map((feature) => (
                    <Card key={feature.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">{feature.module}</Badge>
                              {feature.submodule && (
                                <Badge variant="secondary">{feature.submodule}</Badge>
                              )}
                              {feature.rule_type && (
                                <Badge className="bg-purple-100 text-purple-700">
                                  {feature.rule_type}: {feature.rule_value}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {feature.start_date && `De ${(() => { const m = String(feature.start_date).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : feature.start_date; })()}`}
                              {feature.end_date && ` ate ${(() => { const m = String(feature.end_date).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : feature.end_date; })()}`}
                              {!feature.start_date && !feature.end_date && 'Sem data definida (permanente)'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditFeature(feature)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteFeature(feature.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Switch
                              checked={feature.enabled}
                              onCheckedChange={(checked) => handleToggleFeature(feature.id, checked)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Comunicação */}
            <TabsContent value="comunicacao">
              <ComunicacaoAdmin />
            </TabsContent>

            {/* Documentos */}
            <TabsContent value="documentos">
              <DocumentosAdmin />
            </TabsContent>

            {/* Ferramentas */}
            <TabsContent value="ferramentas">
              <FerramentasAdmin />
            </TabsContent>

            {/* Logs */}
            <TabsContent value="logs">
              <LogsAdmin />
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Modal Criar Usuário */}
      <CreateUserModal 
        open={createUserModal} 
        onClose={() => setCreateUserModal(false)}
        onSuccess={(data) => {
          setCredentialsModal({ type: 'new', ...data });
          loadData();
        }}
      />

      {/* Modal Geração em Massa */}
      <Dialog open={bulkGenerateModal} onOpenChange={setBulkGenerateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerar Usuários em Massa</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Atenção</p>
                  <p>Esta ação criará usuários para todos os responsáveis que ainda não possuem acesso ao portal.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-sm">Enviar por Email</p>
                    <p className="text-xs text-gray-500">Os responsáveis receberão as credenciais por email</p>
                  </div>
                </div>
                <Switch 
                  id="send-email-bulk" 
                  defaultChecked={true}
                />
              </div>
              
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm font-medium text-purple-800 mb-1">URL de Acesso ao Portal:</p>
                <Input 
                  id="portal-url-bulk"
                  defaultValue="https://dnacore.novodna.com.br/familia/login"
                  className="text-sm"
                />
              </div>
            </div>
            
            <p className="text-gray-600 text-sm">
              Serão geradas credenciais automáticas para cada responsável. 
              Certifique-se de que os emails estão corretos no cadastro.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkGenerateModal(false)}>
              Cancelar
            </Button>
            <Button onClick={() => {
              const sendEmail = document.getElementById('send-email-bulk')?.ariaChecked === 'true';
              const portalUrl = document.getElementById('portal-url-bulk')?.value || '';
              handleBulkGenerate(sendEmail, portalUrl);
              setBulkGenerateModal(false);
            }}>
              <Users className="w-4 h-4 mr-2" />
              Gerar Usuários
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Credenciais */}
      <Dialog open={!!credentialsModal} onOpenChange={() => setCredentialsModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {credentialsModal?.type === 'bulk' ? 'Usuários Criados' : 'Credenciais'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {credentialsModal?.type === 'bulk' ? (
              <div className="space-y-3">
                <p className="text-green-600 font-medium">
                  ✓ {credentialsModal.created} usuários criados com sucesso!
                  {credentialsModal.emails_sent > 0 && (
                    <span className="block text-sm text-gray-500 font-normal">
                      {credentialsModal.emails_sent} emails registrados para envio
                    </span>
                  )}
                </p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {credentialsModal.credentials?.map((cred, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded border">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{cred.responsible_name}</p>
                          <p className="text-sm text-gray-600">{cred.email}</p>
                          <p className="font-mono text-purple-600 text-sm">Senha: {cred.password}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              navigator.clipboard.writeText(`Email: ${cred.email}\nSenha: ${cred.password}`);
                              toast.success('Copiado!');
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {cred.whatsapp && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                              onClick={() => {
                                const phone = cred.whatsapp.replace(/\D/g, '');
                                const fullPhone = phone.startsWith('55') ? phone : `55${phone}`;
                                const message = encodeURIComponent(
                                  `Olá ${cred.responsible_name}!\n\n` +
                                  `Seu acesso ao DNA Connect foi criado!\n\n` +
                                  `Acesse: https://dnacore.novodna.com.br/familia/login\n` +
                                  `Email: ${cred.email}\n` +
                                  `Senha: ${cred.password}\n\n` +
                                  `Atenciosamente,\nEquipe da Escola`
                                );
                                window.open(`https://wa.me/${fullPhone}?text=${message}`, '_blank');
                              }}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-2">Senha temporária:</p>
                <p className="text-2xl font-mono font-bold text-purple-700">
                  {credentialsModal?.password || credentialsModal?.temporary_password}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    navigator.clipboard.writeText(credentialsModal?.password || credentialsModal?.temporary_password);
                    toast.success('Senha copiada!');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setCredentialsModal(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação — gerar nova senha p/ envio WhatsApp */}
      <Dialog open={!!whatsappConfirm} onOpenChange={(o) => { if (!o && !whatsappLoading) setWhatsappConfirm(null); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Phone className="w-5 h-5" /> Gerar nova senha e enviar via WhatsApp
            </DialogTitle>
          </DialogHeader>
          {whatsappConfirm && (
            <div className="space-y-2 text-sm">
              <p>
                Deseja gerar uma <strong>nova senha</strong> e enviar via WhatsApp para o responsável
                <strong> {whatsappConfirm.user.responsible_name || whatsappConfirm.user.email}</strong>?
              </p>
              <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                A senha anterior será substituída por uma nova (padrão DNA####XX). O responsável deverá usar a nova senha no próximo acesso.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappConfirm(null)} disabled={whatsappLoading}>Cancelar</Button>
            <Button
              onClick={confirmAndGeneratePassword}
              disabled={whatsappLoading}
              className="bg-green-600 hover:bg-green-700 text-white gap-2"
              data-testid="wa-confirm-generate-btn"
            >
              {whatsappLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              {whatsappLoading ? 'Gerando…' : 'Gerar senha e abrir WhatsApp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal WhatsApp — envio manual de credenciais */}
      <Dialog open={!!whatsappModal} onOpenChange={(o) => { if (!o) setWhatsappModal(null); }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Phone className="w-5 h-5" /> Enviar Credenciais via WhatsApp
            </DialogTitle>
          </DialogHeader>
          {whatsappModal && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-gray-500">Responsável</Label>
                  <div className="font-medium">{whatsappModal.user.responsible_name || '—'}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Telefone</Label>
                  <Input
                    value={whatsappModal.phone}
                    onChange={(e) => setWhatsappModal({ ...whatsappModal, phone: e.target.value })}
                    data-testid="wa-phone-input"
                  />
                </div>
              </div>
              {whatsappModal.password && (
                <div className="rounded-lg border-2 border-green-300 bg-green-50 p-3 flex items-center justify-between gap-2" data-testid="wa-password-badge">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-green-700 font-semibold">Nova senha gerada</div>
                    <div className="text-xl font-mono font-bold text-green-800 mt-0.5">{whatsappModal.password}</div>
                    <div className="text-[11px] text-gray-600 mt-1">Será exibida apenas neste modal — após fechar, somente o hash permanece salvo.</div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => { try { navigator.clipboard.writeText(whatsappModal.password); toast.success('Senha copiada.'); } catch { /* noop */ } }}
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar
                  </Button>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs text-gray-500">Mensagem (editável)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 border-green-300 text-green-700 hover:bg-green-50"
                    onClick={copyWhatsappMessage}
                    data-testid="wa-copy-message-header"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar mensagem
                  </Button>
                </div>
                <textarea
                  value={whatsappModal.message}
                  onChange={(e) => setWhatsappModal({ ...whatsappModal, message: e.target.value })}
                  rows={14}
                  className="w-full border rounded-md p-2 text-sm font-mono whitespace-pre-wrap"
                  data-testid="wa-message-input"
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  Apenas abre a conversa pronta no WhatsApp Web/App — nada é enviado automaticamente.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappModal(null)}>Cancelar</Button>
            <Button
              variant="outline"
              onClick={copyWhatsappMessage}
              className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              data-testid="wa-copy-message-footer"
            >
              <Copy className="w-4 h-4" /> Copiar mensagem
            </Button>
            <Button onClick={confirmWhatsappOpen} className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="wa-open-btn">
              <ExternalLink className="w-4 h-4" /> Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal WhatsApp em massa — lista de links individuais */}
      <Dialog open={whatsappBulkModal} onOpenChange={setWhatsappBulkModal}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Phone className="w-5 h-5" /> Enviar Credenciais WhatsApp — em massa
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 mb-2">
            Clique em <strong>Gerar &amp; Abrir</strong> em cada linha. O sistema vai gerar uma <strong>nova senha</strong> individual e abrir o WhatsApp com a mensagem pronta. Nenhuma mensagem é enviada automaticamente.
          </div>
          <div className="max-h-[60vh] overflow-y-auto space-y-1.5 border rounded-md p-2 bg-gray-50">
            {filteredUsers.filter((u) => !!u.responsible_phone && !!u.email).map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-white border rounded px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.responsible_name}</div>
                  <div className="text-xs text-gray-500 truncate">{u.email} · {u.responsible_phone}</div>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" onClick={() => openBulkLink(u)} data-testid={`btn-bulk-wa-row-${u.id}`}>
                  <Key className="w-3.5 h-3.5" /> Gerar &amp; Abrir
                </Button>
              </div>
            ))}
            {filteredUsers.filter((u) => !!u.responsible_phone && !!u.email).length === 0 && (
              <div className="text-sm text-gray-500 py-6 text-center">Nenhum usuário filtrado com telefone e login cadastrados.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappBulkModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Feature */}
      <FeatureModal
        open={!!featureModal}
        feature={featureModal}
        onClose={() => setFeatureModal(null)}
        onSuccess={() => { setFeatureModal(null); loadData(); }}
      />

      {/* Modal Gerar por Turma */}
      <Dialog open={bulkByClassModal} onOpenChange={setBulkByClassModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Gerar Usuários por Turma</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">Selecione as turmas para criar acesso automaticamente para responsáveis que ainda não possuem usuário.</p>
            <div className="border rounded-lg p-3 max-h-60 overflow-y-auto space-y-1">
              <div className="flex justify-between mb-2">
                <span className="text-xs text-gray-400">{turmasSelecionadas.length} selecionada(s)</span>
                <div className="flex gap-2">
                  <button onClick={() => setTurmasSelecionadas(turmasDisponiveis.map(t => t.id))} className="text-xs text-purple-600 hover:underline">Todas</button>
                  <button onClick={() => setTurmasSelecionadas([])} className="text-xs text-gray-400 hover:underline">Limpar</button>
                </div>
              </div>
              {turmasDisponiveis.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(t => (
                <label key={t.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={turmasSelecionadas.includes(t.id)}
                    onChange={e => setTurmasSelecionadas(prev => e.target.checked ? [...prev, t.id] : prev.filter(id => id !== t.id))}
                    className="rounded border-gray-300" />
                  {t.name}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkByClassModal(false)}>Cancelar</Button>
            <Button disabled={turmasSelecionadas.length === 0}
              onClick={() => { handleBulkGenerateByClass(turmasSelecionadas); setBulkByClassModal(false); }}>
              Gerar Usuários
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente: Criar Usuário
function CreateUserModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [responsibles, setResponsibles] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedResponsible, setSelectedResponsible] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [sendMethod, setSendMethod] = useState('email'); // email, whatsapp, both
  
  // Busca por aluno - PADRÃO agora é 'student'
  const [searchMode, setSearchMode] = useState('student'); // student ou responsible
  const [searchStudent, setSearchStudent] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentResponsibles, setStudentResponsibles] = useState([]);
  const [loadingResps, setLoadingResps] = useState(false);
  const [guardianResults, setGuardianResults] = useState([]);
  const [searchGuardian, setSearchGuardian] = useState('');
  const [existingUser, setExistingUser] = useState(null);

  useEffect(() => {
    if (open) {
      loadResponsibles();
      loadStudents();
      // Reset states
      setSelectedResponsible('');
      setEmail('');
      setWhatsapp('');
      setSearchStudent('');
      setSelectedStudent(null);
      setStudentResponsibles([]);
    }
  }, [open]);

  const asList = (d) => (Array.isArray(d) ? d : (d?.items || d?.students || d?.responsibles || []));

  const loadResponsibles = async () => {
    try {
      const response = await api.get('/responsibles?limit=1000');
      setResponsibles(asList(response.data));
    } catch (error) {
      const real = error?.response?.data?.detail || error?.message;
      toast.error(`Erro ao carregar responsáveis: ${typeof real === 'string' ? real : 'falha na API'}`);
      console.error('[DNA Connect Admin] /responsibles falhou', error?.response?.status, real);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await api.get('/students?limit=1000');
      setStudents(asList(response.data));
    } catch (error) {
      const real = error?.response?.data?.detail || error?.message;
      toast.error(`Erro ao carregar alunos: ${typeof real === 'string' ? real : 'falha na API'}`);
    }
  };

  // Busca canônica de responsáveis (guardians + filhos vinculados)
  const searchGuardians = async (term) => {
    if ((term || '').trim().length < 2) { setGuardianResults([]); return; }
    try {
      const { data } = await api.get(`/guardians/search?q=${encodeURIComponent(term)}&limit=20`);
      setGuardianResults(data.items || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao pesquisar responsáveis');
    }
  };

  // Filtrar alunos pela busca
  const filteredStudents = students.filter(s => 
    searchStudent.length >= 2 && 
    s.name?.toLowerCase().includes(searchStudent.toLowerCase())
  ).slice(0, 10);

  // Responsáveis do aluno: FONTE ÚNICA (guardian_student_links → guardians)
  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setSearchStudent(student.name);
    setStudentResponsibles([]);
    setLoadingResps(true);
    try {
      const { data } = await api.get(`/students/${student.id}/guardians`);
      const resps = (data.items || [])
        .filter(it => it.guardian)
        .map(it => ({
          id: it.responsible_id,
          ...it.guardian,
          roles: {
            financial: !!it.is_financial, legal: !!it.is_legal,
            pedagogical: !!it.is_pedagogical, primary_contact: !!it.is_primary_contact,
            can_pickup: !!it.can_pickup,
          },
          relation: it.relation,
          siblings: it.siblings || [],
        }));
      setStudentResponsibles(resps);
      const preferido = resps.find(r => r.roles.financial) || resps[0];
      if (preferido) {
        setSelectedResponsible(preferido.id);
        setEmail(preferido.email || '');
        setWhatsapp(preferido.whatsapp || preferido.cellphone || '');
      }
    } catch (error) {
      const real = error?.response?.data?.detail || error?.message;
      toast.error(`Erro ao carregar responsáveis do aluno: ${typeof real === 'string' ? real : 'falha na API'}`);
    } finally { setLoadingResps(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setExistingUser(null);
    if (!selectedResponsible) {
      toast.error('Selecione o responsável que terá o acesso.');
      return;
    }
    if (!email?.trim()) {
      toast.error('Responsável sem e-mail cadastrado. Edite o responsável antes de criar o acesso.');
      return;
    }

    setLoading(true);
    try {
      // URL do portal família - usando a URL de produção ou a atual
      const portalUrl = window.location.origin.includes('preview.emergentagent.com') 
        ? `${window.location.origin}/familia/login`
        : 'https://dnacore.novodna.com.br/familia/login';
      
      const response = await api.post('/dnaconnect/admin/usuarios', {
        responsible_id: selectedResponsible,
        email: email,
        whatsapp: whatsapp,
        send_method: sendMethod,
        auto_generate_password: true,
        portal_url: portalUrl,
        send_welcome_email: sendMethod === 'email' || sendMethod === 'both'
      });
      onSuccess(response.data);
      onClose();
      
      if (sendMethod === 'whatsapp' || sendMethod === 'both') {
        // Abrir WhatsApp com a mensagem e URL correta
        const resp = responsibles.find(r => r.id === selectedResponsible);
        const phone = whatsapp || resp?.whatsapp || resp?.cellphone;
        if (phone) {
          const cleanPhone = phone.replace(/\D/g, '');
          const fullPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
          const message = encodeURIComponent(
            `Olá ${resp?.name}!\n\n` +
            `Seu acesso ao DNA Connect foi criado!\n\n` +
            `*Acesse:* ${portalUrl}\n` +
            `*Email:* ${email}\n` +
            `*Senha:* ${response.data.password || 'Verifique seu email'}\n\n` +
            `Por segurança, troque sua senha no primeiro acesso.\n\n` +
            `Atenciosamente,\nEquipe da Escola`
          );
          window.open(`https://wa.me/${fullPhone}?text=${message}`, '_blank');
        }
      }
      
      toast.success('Usuário criado com sucesso!');
    } catch (error) {
      const d = error.response?.data?.detail;
      if (d?.error === 'FAMILY_USER_EXISTS') {
        setExistingUser(d);
        toast.error(d.message);
      } else {
        toast.error(typeof d === 'string' ? d : (d?.message || 'Erro ao criar usuário'));
      }
    } finally {
      setLoading(false);
    }
  };

  const enviarAcesso = async (familyUserId, firstAccess) => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/dnaconnect/admin/usuarios/${familyUserId}/enviar-acesso?first_access=${firstAccess}`);
      toast.success(data.message || 'Link enviado por e-mail.');
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha no envio do e-mail.');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Criar Usuário Família</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Modo de Busca - Por Aluno é padrão */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <Button
              type="button"
              variant={searchMode === 'student' ? 'default' : 'ghost'}
              size="sm"
              className="flex-1"
              onClick={() => setSearchMode('student')}
            >
              Por Aluno
            </Button>
            <Button
              type="button"
              variant={searchMode === 'responsible' ? 'default' : 'ghost'}
              size="sm"
              className="flex-1"
              onClick={() => {
                setSearchMode('responsible');
                setSelectedStudent(null);
                setStudentResponsibles([]);
              }}
            >
              Por Responsável
            </Button>
          </div>

          {/* Busca por Aluno */}
          {searchMode === 'student' && (
            <div className="space-y-2">
              <Label>Buscar Aluno</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Digite o nome do aluno..."
                  value={searchStudent}
                  onChange={(e) => {
                    setSearchStudent(e.target.value);
                    setSelectedStudent(null);
                    setStudentResponsibles([]);
                  }}
                  className="pl-10"
                />
                {searchStudent.length >= 2 && !selectedStudent && filteredStudents.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredStudents.map(student => (
                      <div
                        key={student.id}
                        onClick={() => selectStudent(student)}
                        className="p-3 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                      >
                        <p className="font-medium">{student.name}</p>
                        <p className="text-xs text-gray-500">{student.class_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Responsáveis do aluno */}
              {selectedStudent && studentResponsibles.length > 0 && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800 mb-2">
                    Responsáveis de {selectedStudent.name}:
                  </p>
                  <div className="space-y-2">
                    {studentResponsibles.map(resp => (
                      <label
                        key={resp.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
                          selectedResponsible === resp.id ? 'bg-purple-100 border-purple-300' : 'bg-white'
                        } border`}
                      >
                        <input
                          type="radio"
                          name="responsible"
                          checked={selectedResponsible === resp.id}
                          onChange={() => {
                            setSelectedResponsible(resp.id);
                            if (resp.email) setEmail(resp.email);
                            if (resp.whatsapp || resp.cellphone) setWhatsapp(resp.whatsapp || resp.cellphone);
                          }}
                        />
                        <div>
                          <p className="font-medium text-sm">{resp.name}</p>
                          <p className="text-xs text-gray-500">
                            {resp.email || 'Responsável sem e-mail cadastrado.'}
                            {resp.whatsapp || resp.cellphone ? ` · ${resp.whatsapp || resp.cellphone}` : ''}
                          </p>
                          <p className="text-[10.5px] text-purple-700 font-semibold">
                            {[resp.roles?.financial && 'Financeiro', resp.roles?.pedagogical && 'Pedagógico',
                              resp.roles?.legal && 'Legal', resp.roles?.primary_contact && 'Contato principal',
                              resp.roles?.can_pickup && 'Pode retirar'].filter(Boolean).join(' · ') || resp.relation || 'Responsável'}
                            {resp.siblings?.length ? ` · ${resp.siblings.length} irmão(s) vinculado(s)` : ''}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedStudent && loadingResps && (
                <p className="p-3 text-sm text-gray-500" data-testid="loading-resps">Carregando responsáveis…</p>
              )}
              {selectedStudent && !loadingResps && studentResponsibles.length === 0 && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200" data-testid="sem-responsavel">
                  <p className="text-sm text-yellow-700">
                    Nenhum responsável vinculado a este aluno. Vincule no cadastro do aluno (Aluno 360).
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Busca direta no cadastro de responsáveis (guardians) */}
          {searchMode === 'responsible' && (
            <div>
              <Label>Responsável (nome, CPF, e-mail ou telefone)</Label>
              <Input value={searchGuardian} data-testid="busca-responsavel"
                placeholder="Digite ao menos 2 letras…"
                onChange={(e) => { setSearchGuardian(e.target.value); searchGuardians(e.target.value); }} />
              {guardianResults.length > 0 && (
                <div className="mt-2 space-y-2 max-h-56 overflow-y-auto" data-testid="resultados-responsavel">
                  {guardianResults.map((g) => (
                    <label key={g.id} data-testid={`guardian-opt-${g.id}`}
                      className={`flex items-start gap-3 p-2 rounded border cursor-pointer ${
                        selectedResponsible === g.id ? 'bg-purple-100 border-purple-300' : 'bg-white'}`}>
                      <input type="radio" name="guardian" checked={selectedResponsible === g.id}
                        onChange={() => {
                          setSelectedResponsible(g.id);
                          setEmail(g.email || '');
                          setWhatsapp(g.whatsapp || g.cellphone || '');
                        }} />
                      <div>
                        <p className="font-medium text-sm">{g.name}</p>
                        <p className="text-xs text-gray-500">
                          {g.email || 'Responsável sem e-mail cadastrado.'}
                          {g.cpf_masked ? ` · ${g.cpf_masked}` : ''}
                        </p>
                        {!!g.children?.length && (
                          <p className="text-[10.5px] text-purple-700 font-semibold">
                            Filhos vinculados: {g.children.map(c => c.name).join(', ')}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {!!existingUser && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-2"
              data-testid="usuario-existente">
              <p className="text-sm font-semibold text-amber-900">{existingUser.message}</p>
              <p className="text-xs text-amber-800">{existingUser.email} · status {existingUser.status}</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" disabled={loading} data-testid="btn-reenviar-acesso"
                  onClick={() => enviarAcesso(existingUser.family_user_id, true)}>
                  Reenviar acesso
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={loading}
                  data-testid="btn-redefinir-senha"
                  onClick={() => enviarAcesso(existingUser.family_user_id, false)}>
                  Redefinir senha
                </Button>
                <Button type="button" size="sm" variant="ghost" data-testid="btn-abrir-usuario"
                  onClick={() => { onClose(); }}>
                  Abrir usuário
                </Button>
              </div>
            </div>
          )}
          
          {/* Email */}
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplo.com"
            />
          </div>
          
          {/* WhatsApp */}
          <div>
            <Label>WhatsApp (opcional)</Label>
            <Input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="(11) 99999-9999"
            />
          </div>
          
          {/* Método de Envio */}
          <div>
            <Label className="mb-2 block">Enviar credenciais por:</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={sendMethod === 'email' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSendMethod('email')}
              >
                <Mail className="w-4 h-4 mr-1" />
                Email
              </Button>
              <Button
                type="button"
                variant={sendMethod === 'whatsapp' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSendMethod('whatsapp')}
                className={sendMethod === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                WhatsApp
              </Button>
              <Button
                type="button"
                variant={sendMethod === 'both' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSendMethod('both')}
              >
                Ambos
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Criar Usuário
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Componente: Modal de Feature
function FeatureModal({ open, feature, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    module: '',
    submodule: '',
    enabled: true,
    rule_type: '',
    rule_value: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    if (feature && !feature.isNew) {
      setForm({
        module: feature.module || '',
        submodule: feature.submodule || '',
        enabled: feature.enabled ?? true,
        rule_type: feature.rule_type || '',
        rule_value: feature.rule_value || '',
        start_date: feature.start_date?.split('T')[0] || '',
        end_date: feature.end_date?.split('T')[0] || ''
      });
    } else {
      setForm({
        module: '',
        submodule: '',
        enabled: true,
        rule_type: '',
        rule_value: '',
        start_date: '',
        end_date: ''
      });
    }
  }, [feature]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.module) {
      toast.error('Selecione um módulo');
      return;
    }

    setLoading(true);
    try {
      if (feature?.isNew) {
        await api.post('/dnaconnect/admin/features', form);
      } else {
        await api.put(`/dnaconnect/admin/features/${feature.id}`, form);
      }
      onSuccess();
      toast.success('Regra salva com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar regra');
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    { value: 'inicio', label: 'Início' },
    { value: 'filhos', label: 'Meus Filhos' },
    { value: 'pedagogico', label: 'Pedagógico' },
    { value: 'financeiro', label: 'Financeiro' },
    { value: 'comunicacao', label: 'Comunicação' },
    { value: 'calendario', label: 'Calendário' },
    { value: 'documentos', label: 'Documentos' },
    { value: 'dnago', label: 'DNA GO' }
  ];

  const submodules = {
    pedagogico: ['boletim', 'frequencia', 'ocorrencias', 'avaliacoes', 'agenda', 'tarefas'],
    financeiro: ['mensalidades', 'boletos', 'historico_financeiro'],
    dnago: ['estou_chegando', 'autorizados', 'historico_retiradas']
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{feature?.isNew ? 'Nova Regra' : 'Editar Regra'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Módulo</Label>
            <Select value={form.module} onValueChange={(val) => setForm({ ...form, module: val, submodule: '' })}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o módulo" />
              </SelectTrigger>
              <SelectContent>
                {modules.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {submodules[form.module] && (
            <div>
              <Label>Submódulo (opcional)</Label>
              <Select value={form.submodule || "_all"} onValueChange={(val) => setForm({ ...form, submodule: val === "_all" ? "" : val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  {submodules[form.module].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Tipo de Regra (opcional)</Label>
            <Select value={form.rule_type || "_all"} onValueChange={(val) => setForm({ ...form, rule_type: val === "_all" ? "" : val })}>
              <SelectTrigger>
                <SelectValue placeholder="Aplicar a todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todos</SelectItem>
                <SelectItem value="segment">Por Segmento</SelectItem>
                <SelectItem value="class">Por Turma</SelectItem>
                <SelectItem value="student">Por Aluno</SelectItem>
                <SelectItem value="responsible">Por Responsável</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.rule_type && (
            <div>
              <Label>ID do {form.rule_type}</Label>
              <Input
                value={form.rule_value}
                onChange={(e) => setForm({ ...form, rule_value: e.target.value })}
                placeholder="ID ou identificador"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data Início (opcional)</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Data Fim (opcional)</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={form.enabled}
              onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
            />
            <Label>Funcionalidade {form.enabled ? 'Liberada' : 'Bloqueada'}</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Componente: Comunicação Admin (Versão Completa)
function ComunicacaoAdmin() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [turmas, setTurmas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [searchAluno, setSearchAluno] = useState('');
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'aviso',
    target_type: 'all',
    target_ids: [],
    attachments: [],
    images: [],
    image_url: null,
    image_filename: null,
    image_mime_type: null,
    send_notification: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadMessages();
    loadTurmas();
  }, []);

  useEffect(() => {
    if (formData.target_type === 'student' && searchAluno.length >= 2) {
      loadAlunos(searchAluno);
    }
  }, [searchAluno, formData.target_type]);

  const loadMessages = async () => {
    try {
      const response = await api.get('/dnaconnect/admin/mensagens?limit=50');
      setMessages(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const loadTurmas = async () => {
    try {
      const response = await api.get('/dnaconnect/admin/mensagens/destinatarios/turmas');
      setTurmas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const loadAlunos = async (search) => {
    try {
      const response = await api.get(`/dnaconnect/admin/mensagens/destinatarios/alunos?search=${search}&limit=20`);
      setAlunos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  };

  const openNewMessage = () => {
    setEditingMessage(null);
    setFormData({
      title: '',
      content: '',
      type: 'aviso',
      target_type: 'all',
      target_ids: [],
      attachments: [],
      images: [],
      image_url: null,
      image_filename: null,
      image_mime_type: null,
      send_notification: true
    });
    setShowModal(true);
  };

  const openEditMessage = (msg) => {
    setEditingMessage(msg);
    setFormData({
      title: msg.title,
      content: msg.content,
      type: msg.type || 'aviso',
      target_type: msg.target_type || 'all',
      target_ids: msg.target_ids || [],
      attachments: msg.attachments || [],
      images: (msg.images && msg.images.length)
        ? msg.images
        : (msg.image_url ? [{ url: msg.image_url, filename: msg.image_filename, mime_type: msg.image_mime_type }] : []),
      image_url: msg.image_url || null,
      image_filename: msg.image_filename || null,
      image_mime_type: msg.image_mime_type || null,
      send_notification: false
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    setUploadingImage(true);
    const novas = [];
    let erros = 0;
    for (const file of files) {
      if (!allowed.includes(file.type)) {
        toast.error(`${file.name}: formato inválido. Use JPG, JPEG, PNG ou WEBP.`);
        erros += 1;
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: maior que 5MB.`);
        erros += 1;
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/dnaconnect/admin/mensagens/upload-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        novas.push({
          url: res.data.image_url,
          filename: res.data.image_filename,
          mime_type: res.data.image_mime_type
        });
      } catch (err) {
        erros += 1;
        toast.error(err?.response?.data?.detail || `Falha no upload de ${file.name}`);
      }
    }
    if (novas.length) {
      setFormData(prev => {
        const todas = [...(prev.images || []), ...novas];
        return {
          ...prev,
          images: todas,
          image_url: todas[0].url,
          image_filename: todas[0].filename,
          image_mime_type: todas[0].mime_type
        };
      });
      toast.success(novas.length === 1 ? 'Imagem carregada' : `${novas.length} imagens carregadas`);
    }
    setUploadingImage(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const todas = (prev.images || []).filter((_, i) => i !== index);
      return {
        ...prev,
        images: todas,
        image_url: todas[0]?.url || null,
        image_filename: todas[0]?.filename || null,
        image_mime_type: todas[0]?.mime_type || null
      };
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Preencha o título e conteúdo');
      return;
    }

    setSaving(true);
    try {
      if (editingMessage) {
        await api.put(`/dnaconnect/admin/mensagens/${editingMessage.id}`, formData);
        toast.success('Mensagem atualizada!');
      } else {
        await api.post('/dnaconnect/admin/mensagens/v2', formData);
        toast.success('Mensagem enviada!');
      }
      setShowModal(false);
      loadMessages();
    } catch (error) {
      toast.error('Erro ao salvar mensagem');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
    
    try {
      await api.delete(`/dnaconnect/admin/mensagens/${msgId}`);
      toast.success('Mensagem excluída');
      loadMessages();
    } catch (error) {
      toast.error('Erro ao excluir mensagem');
    }
  };

  const toggleTurma = (turmaId) => {
    setFormData(prev => ({
      ...prev,
      target_ids: prev.target_ids.includes(turmaId)
        ? prev.target_ids.filter(id => id !== turmaId)
        : [...prev.target_ids, turmaId]
    }));
  };

  const toggleAluno = (alunoId) => {
    setFormData(prev => ({
      ...prev,
      target_ids: prev.target_ids.includes(alunoId)
        ? prev.target_ids.filter(id => id !== alunoId)
        : [...prev.target_ids, alunoId]
    }));
  };

  const getTargetLabel = (msg) => {
    if (msg.target_type === 'all') return 'Todas as famílias';
    if (msg.target_type === 'class') return `${msg.target_ids?.length || 0} turma(s)`;
    if (msg.target_type === 'student') return `${msg.target_ids?.length || 0} aluno(s)`;
    return msg.target_type;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'urgente': return 'bg-red-100 text-red-700 border-red-200';
      case 'informativo': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-purple-100 text-purple-700 border-purple-200';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Mensagens Enviadas</h2>
          <p className="text-sm text-gray-500">{messages.length} mensagem(s)</p>
        </div>
        <Button onClick={openNewMessage} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Nova Mensagem
        </Button>
      </div>

      {messages.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nenhuma mensagem enviada</p>
            <Button onClick={openNewMessage} className="mt-4">
              Enviar primeira mensagem
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {messages.map((msg) => (
            <Card key={msg.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getTypeColor(msg.type)}>{msg.type}</Badge>
                      <Badge variant="outline">{getTargetLabel(msg)}</Badge>
                      {msg.attachments?.length > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <Paperclip className="w-3 h-3" />
                          {msg.attachments.length}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-900">{msg.title}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{msg.content}</p>
                    {msg.target_names?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Destinatários: {msg.target_names.slice(0, 3).join(', ')}
                        {msg.target_names.length > 3 && ` +${msg.target_names.length - 3}`}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(msg.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {msg.read_count || 0} leitura(s)
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button variant="ghost" size="sm" onClick={() => openEditMessage(msg)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(msg.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Nova/Editar Mensagem */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMessage ? 'Editar Mensagem' : 'Nova Mensagem'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Tipo */}
            <div className="space-y-2">
              <Label>Tipo de Mensagem</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aviso">Aviso</SelectItem>
                  <SelectItem value="informativo">Informativo</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Título */}
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                placeholder="Título da mensagem..."
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>

            {/* Conteúdo */}
            <div className="space-y-2">
              <Label>Conteúdo *</Label>
              <textarea
                className="w-full min-h-[120px] p-3 border rounded-md resize-y"
                placeholder="Digite o conteúdo da mensagem..."
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
              />
            </div>

            {/* Imagens do comunicado (múltiplas) */}
            <div className="space-y-2 rounded-lg border border-purple-100 bg-purple-50/40 p-3" data-testid="msg-image-block">
              <Label className="text-purple-800 font-semibold">
                Imagens do comunicado {formData.images?.length > 0 && `(${formData.images.length})`}
              </Label>

              {formData.images?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="msg-image-grid">
                  {formData.images.map((img, idx) => (
                    <div key={`${img.url}-${idx}`} className="relative group rounded-lg overflow-hidden border bg-white">
                      <img
                        src={img.url}
                        alt={img.filename || `imagem ${idx + 1}`}
                        className="h-24 w-full object-cover"
                        loading="lazy"
                        data-testid={`msg-image-preview-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        title="Remover imagem"
                        aria-label={`Remover imagem ${idx + 1}`}
                        data-testid={`msg-image-remove-${idx}`}
                        className="absolute top-1 right-1 rounded-full bg-white/90 border border-red-200 text-red-600 p-1 shadow-sm hover:bg-red-50"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <p className="text-[10px] text-slate-500 truncate px-1.5 py-1">{img.filename}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <input
                  id="msg-image-input"
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="msg-image-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('msg-image-input').click()}
                  disabled={uploadingImage}
                  data-testid="msg-image-add"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  {uploadingImage ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
                  ) : (
                    <>{formData.images?.length > 0 ? 'Adicionar mais imagens' : 'Anexar imagens'}</>
                  )}
                </Button>
                <span className="text-xs text-slate-500">JPG, JPEG, PNG ou WEBP · até 5MB cada · pode selecionar várias</span>
              </div>
            </div>

            {/* Destinatários */}
            <div className="space-y-2">
              <Label>Enviar para</Label>
              <Select 
                value={formData.target_type} 
                onValueChange={(v) => setFormData({...formData, target_type: v, target_ids: []})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Famílias</SelectItem>
                  <SelectItem value="class">Por Turma</SelectItem>
                  <SelectItem value="student">Por Aluno</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Seleção de Turmas */}
            {formData.target_type === 'class' && (
              <div className="space-y-2">
                <Label>Selecione as Turmas ({formData.target_ids.length} selecionada(s))</Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {turmas.map((turma) => (
                    <label
                      key={turma.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        formData.target_ids.includes(turma.id) ? 'bg-purple-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.target_ids.includes(turma.id)}
                        onChange={() => toggleTurma(turma.id)}
                        className="rounded"
                      />
                      <span className="text-sm">{turma.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Seleção de Alunos */}
            {formData.target_type === 'student' && (
              <div className="space-y-2">
                <Label>Buscar Alunos</Label>
                <Input
                  placeholder="Digite o nome do aluno..."
                  value={searchAluno}
                  onChange={(e) => setSearchAluno(e.target.value)}
                />
                {formData.target_ids.length > 0 && (
                  <p className="text-sm text-purple-600">{formData.target_ids.length} aluno(s) selecionado(s)</p>
                )}
                {alunos.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {alunos.map((aluno) => (
                      <label
                        key={aluno.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                          formData.target_ids.includes(aluno.id) ? 'bg-purple-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.target_ids.includes(aluno.id)}
                          onChange={() => toggleAluno(aluno.id)}
                          className="rounded"
                        />
                        <div>
                          <span className="text-sm font-medium">{aluno.name}</span>
                          {aluno.class_name && (
                            <span className="text-xs text-gray-500 ml-2">({aluno.class_name})</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notificação */}
            {!editingMessage && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.send_notification}
                  onCheckedChange={(v) => setFormData({...formData, send_notification: v})}
                />
                <Label>Enviar notificação push para as famílias</Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                editingMessage ? 'Salvar Alterações' : 'Enviar Mensagem'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Import adicional necessário
const Paperclip = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
);

// Componente: Ferramentas Admin
function FerramentasAdmin() {
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImportarFotos = async () => {
    if (!window.confirm('Deseja importar as fotos da planilha para as carteirinhas dos alunos?\n\nEsta ação irá vincular as fotos do Google Drive aos perfis dos alunos.')) {
      return;
    }

    setImportLoading(true);
    setImportResult(null);
    
    try {
      const response = await api.post('/admin/importar-fotos-planilha');
      setImportResult({
        success: true,
        ...response.data
      });
      toast.success(`${response.data.vinculados} fotos importadas com sucesso!`);
    } catch (error) {
      console.error('Erro ao importar fotos:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.detail || 'Erro ao importar fotos'
      });
      toast.error('Erro ao importar fotos');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Ferramentas Administrativas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Importar Fotos */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Image className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Importar Fotos da Planilha</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Vincula automaticamente as fotos do Google Drive aos perfis dos alunos 
                  usando os dados da planilha de matrícula.
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  • 398 alunos na planilha de importação<br/>
                  • Fotos serão exibidas nas carteirinhas digitais e físicas
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                onClick={handleImportarFotos} 
                disabled={importLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {importLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importar Fotos
                  </>
                )}
              </Button>
            </div>

            {/* Resultado da importação */}
            {importResult && (
              <div className={`mt-4 p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {importResult.success ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">{importResult.message}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div className="text-center p-2 bg-white rounded">
                        <p className="text-2xl font-bold text-green-600">{importResult.vinculados}</p>
                        <p className="text-xs text-gray-500">Fotos vinculadas</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded">
                        <p className="text-2xl font-bold text-blue-600">{importResult.total_planilha}</p>
                        <p className="text-xs text-gray-500">Total na planilha</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded">
                        <p className="text-2xl font-bold text-orange-600">{importResult.nao_encontrados}</p>
                        <p className="text-xs text-gray-500">Não encontrados</p>
                      </div>
                    </div>
                    
                    {importResult.lista_nao_encontrados?.length > 0 && (
                      <details className="mt-3">
                        <summary className="text-sm text-orange-600 cursor-pointer hover:underline">
                          Ver alunos não encontrados ({importResult.nao_encontrados})
                        </summary>
                        <div className="mt-2 max-h-40 overflow-y-auto bg-white p-2 rounded text-xs">
                          {importResult.lista_nao_encontrados.map((nome, i) => (
                            <div key={i} className="py-1 border-b last:border-0">{nome}</div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-5 h-5" />
                    <span>{importResult.error}</span>
                  </div>
                )}
              </div>
            )}
          </div>

        </CardContent>
      </Card>
    </div>
  );
}

// Componente: Logs Admin
function LogsAdmin() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logType, setLogType] = useState('access');

  useEffect(() => {
    loadLogs();
  }, [logType]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const endpoint = logType === 'access' ? '/dnaconnect/admin/logs/access' : '/dnaconnect/admin/logs/features';
      const response = await api.get(`${endpoint}?limit=50`);
      setLogs(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={logType} onValueChange={setLogType}>
        <TabsList>
          <TabsTrigger value="access">Logs de Acesso</TabsTrigger>
          <TabsTrigger value="features">Logs de Alterações</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : logs.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nenhum log encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Data/Hora</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Ação</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 px-4 text-sm">
                        {new Date(log.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="py-2 px-4">
                        <Badge variant="outline">{log.action}</Badge>
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600">
                        {log.user_id || log.feature_id || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
